import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, Clock, DollarSign, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { caseStudiesData } from '../data/mock';

export const CaseStudies = () => {
  const [activeCase, setActiveCase] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const nextCase = () => {
    setActiveCase((prev) => (prev + 1) % caseStudiesData.length);
  };

  const prevCase = () => {
    setActiveCase((prev) => (prev - 1 + caseStudiesData.length) % caseStudiesData.length);
  };

  const currentCase = caseStudiesData[activeCase];

  return (
    <section id="case-studies" ref={sectionRef} className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-20 w-40 h-40 rounded-full opacity-5 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-primary), transparent)`,
          animationDelay: '2s'
        }}></div>
        <div className="absolute bottom-32 left-16 w-32 h-32 rounded-full opacity-10 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-purple), transparent)`,
          animationDelay: '4s'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6" style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-primary)'
          }}>
            <TrendingUp className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <span className="body-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
              Success Stories
            </span>
          </div>
          <h2 className="display-md mb-6" style={{ color: 'var(--text-primary)' }}>
            Real Results from Real Businesses
          </h2>
          <p className="body-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Discover how companies across different industries have transformed their operations 
            and achieved remarkable ROI with our automation solutions.
          </p>
        </div>

        {/* Case Study Showcase */}
        <div className={`max-w-6xl mx-auto transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Case Study Content */}
            <div className="order-2 lg:order-1">
              {/* Company Info */}
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)'
                }}>
                  <Building2 className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
                </div>
                <div>
                  <h3 className="h3 mb-1" style={{ color: 'var(--text-primary)' }}>
                    {currentCase.company}
                  </h3>
                  <p className="body-sm" style={{ color: 'var(--text-muted)' }}>
                    {currentCase.industry}
                  </p>
                </div>
              </div>

              {/* Challenge */}
              <div className="mb-6">
                <h4 className="h4 mb-3" style={{ color: 'var(--text-primary)' }}>
                  The Challenge
                </h4>
                <p className="body-md" style={{ color: 'var(--text-secondary)' }}>
                  {currentCase.challenge}
                </p>
              </div>

              {/* Solution */}
              <div className="mb-6">
                <h4 className="h4 mb-3" style={{ color: 'var(--text-primary)' }}>
                  Our Solution
                </h4>
                <p className="body-md" style={{ color: 'var(--text-secondary)' }}>
                  {currentCase.solution}
                </p>
              </div>

              {/* Results */}
              <div className="mb-8">
                <h4 className="h4 mb-4" style={{ color: 'var(--text-primary)' }}>
                  Results Achieved
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 rounded-xl" style={{ background: 'var(--bg-secondary)' }}>
                    <Clock className="w-6 h-6 mb-2" style={{ color: 'var(--accent-primary)' }} />
                    <p className="body-sm" style={{ color: 'var(--text-secondary)' }}>
                      {currentCase.results}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl" style={{ background: 'var(--bg-secondary)' }}>
                    <DollarSign className="w-6 h-6 mb-2" style={{ color: 'var(--accent-primary)' }} />
                    <p className="h4 mb-1" style={{ color: 'var(--accent-primary)' }}>
                      {currentCase.savings}
                    </p>
                    <p className="body-sm" style={{ color: 'var(--text-muted)' }}>
                      Cost Savings
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Button 
                className="btn-primary"
                onClick={() => {
                  const element = document.querySelector('#contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Start Your Success Story
              </Button>
            </div>

            {/* Visual/Image Section */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative rounded-2xl overflow-hidden" style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-subtle)'
              }}>
                {/* Placeholder for case study visualization */}
                <div className="aspect-video p-8 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center animate-pulse-glow" style={{
                      background: 'var(--accent-primary)'
                    }}>
                      <TrendingUp className="w-12 h-12" style={{ color: 'var(--bg-primary)' }} />
                    </div>
                    <h4 className="h4 mb-2" style={{ color: 'var(--text-primary)' }}>
                      Success Metrics
                    </h4>
                    <p className="body-md" style={{ color: 'var(--text-secondary)' }}>
                      Interactive dashboard showing real-time automation performance
                    </p>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute top-4 right-4 w-3 h-3 rounded-full animate-float" style={{
                  background: 'var(--accent-primary)',
                  animationDelay: '1s'
                }}></div>
                <div className="absolute bottom-4 left-4 w-2 h-2 rounded-full animate-float" style={{
                  background: 'var(--accent-purple)',
                  animationDelay: '2s'
                }}></div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-12">
            {/* Case Indicators */}
            <div className="flex space-x-2">
              {caseStudiesData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveCase(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    activeCase === index ? 'w-8' : ''
                  }`}
                  style={{
                    background: activeCase === index ? 'var(--accent-primary)' : 'var(--border-primary)'
                  }}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={prevCase}
                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <ChevronLeft className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
              </button>
              <button
                onClick={nextCase}
                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <ChevronRight className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
              </button>
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 transition-all duration-1000 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {[
            { number: "500+", label: "Projects Completed" },
            { number: "98%", label: "Success Rate" },
            { number: "$50M+", label: "Client Savings" },
            { number: "24/7", label: "Support Available" }
          ].map((stat, index) => (
            <div key={index} className="text-center p-6 rounded-xl" style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)'
            }}>
              <div className="h2 mb-2" style={{ color: 'var(--accent-primary)' }}>
                {stat.number}
              </div>
              <div className="body-sm" style={{ color: 'var(--text-muted)' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};